import { useEffect, useState } from 'react';
import { fetchIncidents, getIncidentStats, updateIncident, exportIncidentsCsv } from '../utils/adminIncidentApi';
import { useNavigate } from 'react-router-dom';
import Notification from '../components/Notification';
import {
  AlertTriangle,
  RefreshCcw,
  Download,
  Edit2,
  CheckCircle,
  AlertCircle,
  Clock,
  TrendingUp,
  X,
} from 'lucide-react';
import Button from '../components/elements/Button';
import Loading from '../components/elements/Loading';
import Layout from '../components/Layout';
import Input from '../components/elements/Input';
import JsonDetailsModal from '../components/JsonDetailsModal';
import ConfirmModal from '../components/ConfirmModal';
import { toDate } from '../utils/globalUtils';

// How many rows one visit loads. The filters are client-side, so this is also the ceiling on
// what they can find - which is why hitting it is said out loud under the filter row rather
// than left to look like "no such incidents" (EL-36).
const INCIDENT_FETCH_LIMIT = 200;

const AdminIncidentsPage = () => {
  const [incidents, setIncidents] = useState([]);
  const [filteredIncidents, setFilteredIncidents] = useState([]);
  const [stats, setStats] = useState(null);
  const [jsonText, setJsonText] = useState('');
  const [pageReady, setPageReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const isLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true';
  const [notification, setNotification] = useState({ message: '', type: 'success' });
  // The page still OPENS on the open incidents, because that is the useful default for a
  // register. What changed (EL-36) is that this is now a selection in the dropdown rather
  // than a filter baked into the fetch, so the other three statuses have something to match.
  const [filterStatus, setFilterStatus] = useState('OPEN');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterType, setFilterType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  useEffect(() => {
    document.title = 'Incidents | Eternal Locker™';
    if (!isLoggedIn) {
      navigate('/');
      return;
    }
    getIncidents();
    getStats();
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    // Filter incidents based on criteria
    let filtered = incidents;

    if (filterStatus) {
      filtered = filtered.filter(inc => inc.status === filterStatus);
    }

    if (filterSeverity) {
      filtered = filtered.filter(inc => inc.severity === filterSeverity);
    }

    if (filterType) {
      filtered = filtered.filter(inc => inc.incidentType === filterType);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(inc =>
        inc.description.toLowerCase().includes(term) ||
        inc.incidentType.toLowerCase().includes(term) ||
        inc.affectedData?.toLowerCase().includes(term) ||
        (inc.affectedUserId && inc.affectedUserId.toString().includes(term))
      );
    }

    setFilteredIncidents(filtered);
  }, [incidents, filterStatus, filterSeverity, filterType, searchTerm]);

  const getIncidents = async () => {
    setLoading(true);
    setNotification({ message: '', type: 'success' });

    if (!isLoggedIn) {
      setNotification({ message: 'Not authenticated. Please login as admin.', type: 'error' });
      setLoading(false);
      return;
    }

    // EVERY STATUS, ONCE (EL-36). This used to ask the server for `status: 'OPEN'` and then
    // filter that answer in the browser - so picking Investigating, Resolved or Escalated
    // filtered a list that could not contain them and always came back empty, and "All
    // Statuses" showed the open ones while the stats cards above counted the whole table.
    // The filters below are client-side and instant; what they filter has to be the whole
    // register for that to mean anything.
    const data = await fetchIncidents({ limit: INCIDENT_FETCH_LIMIT });
    if (data.success) {
      setIncidents(data.incidents);
      setFilteredIncidents(data.incidents);
      setPageReady(true);
    } else {
      setNotification({ message: data.message || 'Failed to fetch incidents.', type: 'error' });
    }
    setLoading(false);
  };

  const getStats = async () => {
    const statsData = await getIncidentStats();
    if (statsData.success) {
      setStats(statsData.stats);
    }
  };

  const handleOpenEditModal = (incident) => {
    setSelectedIncident(incident);
    setEditFormData({
      status: incident.status,
      actionTaken: incident.actionTaken || '',
      resolution: incident.resolution || '',
      notificationSent: !!incident.notificationSent,
    });
    setEditModalOpen(true);
  };

  const handleSaveIncident = async () => {
    if (!selectedIncident) return;

    const result = await updateIncident(selectedIncident.id, editFormData);
    if (result.success) {
      setNotification({ message: 'Incident updated successfully.', type: 'success' });
      // Update local state
      setIncidents(prevIncidents =>
        prevIncidents.map(inc =>
          inc.id === selectedIncident.id
            ? { ...inc, ...editFormData }
            : inc
        )
      );
      setEditModalOpen(false);
      setSelectedIncident(null);
    } else {
      setNotification({ message: result.message || 'Failed to update incident.', type: 'error' });
    }
  };

  const handleRefresh = () => {
    getIncidents();
    getStats();
  };

  const handleExport = async () => {
    try {
      const blob = await exportIncidentsCsv();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `incident-register-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      setNotification({ message: 'Incident register exported successfully.', type: 'success' });
    } catch (err) {
      setNotification({ message: 'Failed to export incidents.', type: 'error' });
    }
  };

  const getSeverityBadge = (severity) => {
    const severityStyles = {
      CRITICAL: 'bg-red-500/20 text-red-400 border border-red-500/30',
      HIGH: 'bg-orange-500/20 text-orange-400 border border-orange-500/30',
      MEDIUM: 'bg-yellow-500/20 text-yellow-500 border border-yellow-500/30',
      LOW: 'bg-accent-main/20 text-accent-main border border-accent-main/30',
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${severityStyles[severity] || 'bg-accent-main/10 text-accent-main border border-accent-main/20'}`}>
        {severity}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      OPEN: 'bg-red-500/20 text-red-400 border border-red-500/30',
      INVESTIGATING: 'bg-yellow-500/20 text-yellow-500 border border-yellow-500/30',
      RESOLVED: 'bg-accent-main/20 text-accent-main border border-accent-main/30',
      ESCALATED: 'bg-orange-500/20 text-orange-400 border border-orange-500/30',
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusStyles[status] || 'bg-accent-main/10 text-accent-main border border-accent-main/20'}`}>
        {status}
      </span>
    );
  };

  const handleGetIncidentDetails = (incident) => {
    setJsonText(JSON.stringify(incident, null, 2));
  };

  if (loading && !pageReady) {
    return (
      <Layout full={true}>
        <Loading fullScreen={false} />
      </Layout>
    );
  }

  return (
    <Layout full={true}>
      <Notification
        message={notification.message}
        onClose={() => setNotification({ message: '', type: 'success' })}
        type={notification.type}
      />

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Security Incidents</h1>
        <div className="flex gap-2">
          <Button onClick={handleRefresh} variant="secondary">
            <RefreshCcw size={18} /> Refresh
          </Button>
          <Button onClick={handleExport} variant="secondary">
            <Download size={18} /> Export CSV
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-primary-light border border-accent-main rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-75">Total Incidents</p>
                <p className="text-2xl font-bold">{stats.totalIncidents || 0}</p>
              </div>
              <TrendingUp className="text-accent-main" size={28} />
            </div>
          </div>

          <div className="bg-primary-light border border-accent-main rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-75">Open</p>
                <p className="text-2xl font-bold">{stats.openIncidents || 0}</p>
              </div>
              <Clock className="text-accent-main" size={28} />
            </div>
          </div>

          <div className="bg-primary-light border border-accent-main rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-75">Critical</p>
                <p className="text-2xl font-bold">{stats.criticalCount || 0}</p>
              </div>
              <AlertTriangle className="text-accent-main" size={28} />
            </div>
          </div>

          <div className="bg-primary-light border border-accent-main rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-75">Notified</p>
                <p className="text-2xl font-bold">{stats.notifiedCount || 0}</p>
              </div>
              <AlertCircle className="text-accent-main" size={28} />
            </div>
          </div>

          <div className="bg-primary-light border border-accent-main rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-75">Last Incident</p>
                <p className="text-sm font-bold">
                  {stats.lastIncident ? toDate(stats.lastIncident) : 'N/A'}
                </p>
              </div>
              <CheckCircle className="text-accent-main" size={28} />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-primary-light border border-accent-main rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Input
            type="text"
            placeholder="Search incidents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />

          <Input
            type="select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="OPEN">Open</option>
            <option value="INVESTIGATING">Investigating</option>
            <option value="RESOLVED">Resolved</option>
            <option value="ESCALATED">Escalated</option>
          </Input>

          <Input
            type="select"
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value)}
          >
            <option value="">All Severities</option>
            <option value="CRITICAL">Critical</option>
            <option value="HIGH">High</option>
            <option value="MEDIUM">Medium</option>
            <option value="LOW">Low</option>
          </Input>

          <Input
            type="select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="">All Types</option>
            <option value="BREACH">Breach</option>
            <option value="SUSPICIOUS">Suspicious</option>
            <option value="FAILED_AUTH">Failed Auth</option>
            <option value="UNAUTHORIZED_ACCESS">Unauthorized Access</option>
            <option value="DATA_EXFILTRATION">Data Exfiltration</option>
            <option value="POLICY_VIOLATION">Policy Violation</option>
            <option value="OTHER">Other</option>
          </Input>
        </div>
        <p className="text-xs opacity-60 mt-2">
          Showing {filteredIncidents.length} of {incidents.length} incidents
          {incidents.length >= INCIDENT_FETCH_LIMIT && (
            <span> — the {INCIDENT_FETCH_LIMIT} most recent are loaded; older ones are in the CSV export.</span>
          )}
        </p>
      </div>

      {/* Incidents Table */}
      <div className="bg-primary-light rounded-lg border border-accent-main overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-accent-main border-b border-accent-main">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">ID</th>
              <th className="px-4 py-3 text-left font-semibold">Type</th>
              <th className="px-4 py-3 text-left font-semibold">Severity</th>
              <th className="px-4 py-3 text-left font-semibold">Status</th>
              <th className="px-4 py-3 text-left font-semibold">Description</th>
              <th className="px-4 py-3 text-left font-semibold">Affected User</th>
              <th className="px-4 py-3 text-left font-semibold">Created</th>
              <th className="px-4 py-3 text-center font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredIncidents.length > 0 ? (
              filteredIncidents.map((incident) => (
                <tr key={incident.id} className="border-b border-accent-main opacity-75 hover:opacity-100">
                  <td className="px-4 py-3 font-mono text-xs">{incident.id}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 rounded text-xs font-semibold bg-accent-main/20 text-accent-main border border-accent-main/30">
                      {incident.incidentType}
                    </span>
                  </td>
                  <td className="px-4 py-3">{getSeverityBadge(incident.severity)}</td>
                  <td className="px-4 py-3">{getStatusBadge(incident.status)}</td>
                  <td className="px-4 py-3 max-w-xs truncate">
                    {incident.description}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {incident.affectedUserId ? (
                      <span className="px-2 py-1 rounded-full bg-accent-main/20 text-accent-main border border-accent-main/30 text-xs font-semibold">
                        User #{incident.affectedUserId}
                      </span>
                    ) : (
                      <span className="opacity-50">System</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {toDate(incident.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => handleOpenEditModal(incident)}
                        className="text-accent-main hover:opacity-75"
                        title="Edit incident"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleGetIncidentDetails(incident)}
                        className="opacity-60 hover:opacity-100"
                        title="View details"
                      >
                        <AlertCircle size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="px-4 py-8 text-center opacity-60">
                  No incidents found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Incident Modal */}
      {editModalOpen && selectedIncident && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card-background text-primary-dark border border-accent-main rounded-lg p-6 max-w-md w-full max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">
                Update Incident #{selectedIncident.id}
              </h2>
              <button
                onClick={() => setEditModalOpen(false)}
                className="opacity-60 hover:opacity-100"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1">
                  Status
                </label>
                <select
                  value={editFormData.status}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, status: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-accent-main rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent-main bg-background-light text-primary-dark"
                >
                  <option value="OPEN">Open</option>
                  <option value="INVESTIGATING">Investigating</option>
                  <option value="RESOLVED">Resolved</option>
                  <option value="ESCALATED">Escalated</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1">
                  Action Taken
                </label>
                <textarea
                  value={editFormData.actionTaken}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, actionTaken: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-accent-main rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent-main bg-background-light text-primary-dark"
                  rows="3"
                  placeholder="Describe the action taken..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1">
                  Resolution
                </label>
                <textarea
                  value={editFormData.resolution}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, resolution: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-accent-main rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent-main bg-background-light text-primary-dark"
                  rows="3"
                  placeholder="Describe the resolution..."
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editFormData.notificationSent}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      notificationSent: e.target.checked,
                    })
                  }
                  id="notificationSent"
                  className="rounded border-accent-main focus:ring-2 focus:ring-accent-main"
                />
                <label htmlFor="notificationSent" className="text-sm">
                  Breach notification sent to affected users
                </label>
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <Button onClick={handleSaveIncident} className="flex-1">
                Save Changes
              </Button>
              <Button
                onClick={() => setEditModalOpen(false)}
                variant="secondary"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* JSON Details Modal */}
      <JsonDetailsModal jsonText={jsonText} onClose={() => setJsonText('')} />
    </Layout>
  );
};

export default AdminIncidentsPage;
